<template>
  <div class="profile-container">
    <div class="profile-header">
      <h1 class="profile-title">Configuración</h1>
    </div>

    <div v-if="user" class="profile-content">
      <!-- Update Profile Section -->
      <div class="profile-section">
        <h2 class="section-title">Actualizar perfil</h2>
        <div class="profile-photo-section">
          <div class="profile-avatar-container">
            <img 
              v-if="profileImage" 
              :src="profileImage" 
              alt="Foto de perfil" 
              class="profile-avatar"
            >
            <div v-else class="profile-avatar-placeholder">
              <i class="fas fa-user"></i>
            </div>
          </div>
          <div class="photo-actions">
            <button class="btn-change-photo" @click="triggerFileInput">
              Cambiar foto
            </button>
            <p class="photo-requirements">
              Requisitos del archivo: jpg, gif, o png. Tamaño máximo: 500kb.
            </p>
            <input 
              ref="fileInput"
              type="file"
              accept=".jpg,.jpeg,.gif,.png"
              @change="handlePhotoChange"
              style="display: none"
            >
          </div>
        </div>
      </div>

      <!-- Contact Information Section -->
      <div class="profile-section">
        <h2 class="section-title">Información de contacto</h2>
        <div class="form-group">
          <label class="form-label">Nombre completo*</label>
          <input 
            type="text" 
            class="form-input"
            placeholder="Ingresa tu nombre completo"
            v-model="fullName"
            :disabled="!isEditing"
          >
        </div>
      </div>

      <!-- Email Address Section -->
      <div class="profile-section">
        <h2 class="section-title">Dirección de correo electrónico</h2>
        <div class="email-section">
          <span class="email-display">{{ user.email }}</span>
          <button class="btn-modify-email" @click="toggleEmailEdit">
            {{ isEditingEmail ? 'Cancelar' : 'Modificar correo' }}
          </button>
        </div>
        <div v-if="isEditingEmail" class="form-group">
          <input 
            type="email" 
            class="form-input"
            placeholder="Ingresa tu nuevo correo electrónico"
            v-model="newEmail"
          >
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="profile-actions">
        <button class="btn-discard" @click="discardChanges">
          Descartar cambios
        </button>
        <button class="btn-apply" @click="applyChanges">
          Aplicar cambios
        </button>
      </div>
    </div>

    <div v-else class="loading-state">
      <p>Cargando información del perfil...</p>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapGetters, mapActions } from 'vuex';

export default defineComponent({
  name: 'Profile-view',
  created() {
    this.$store.dispatch('viewMe');
  },
  computed: {
    ...mapGetters({ user: 'stateUser' }),
    fullName: {
      get() {
        if (!this.user) return '';
        return this.user.first_name + (this.user.last_name ? ' ' + this.user.last_name : '');
      },
      set(val) {
        const [first, ...rest] = val.split(' ');
        this.editedFirstName = first;
        this.editedLastName = rest.join(' ');
      }
    }
  },
  data() {
    return {
      profileImage: '',
      editedFirstName: '',
      editedLastName: '',
      isEditing: false,
      isEditingEmail: false,
      newEmail: '',
      originalEmail: '',
    };
  },
  watch: {
    user: {
      immediate: true,
      handler(user) {
        if (user) {
          this.profileImage = user.avatar_url || '';
          this.editedFirstName = user.first_name || '';
          this.editedLastName = user.last_name || '';
          this.originalEmail = user.email || '';
          this.newEmail = user.email || '';
        }
      }
    }
  },
  methods: {
    ...mapActions(['deleteUser']),
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handlePhotoChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type) || file.size > 500 * 1024) {
        alert('Archivo no válido. Solo jpg, gif o png y máximo 500kb.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.profileImage = ev.target.result;
      };
      reader.readAsDataURL(file);
    },
    toggleEmailEdit() {
      this.isEditingEmail = !this.isEditingEmail;
      if (!this.isEditingEmail) {
        this.newEmail = this.originalEmail;
      }
    },
    discardChanges() {
      if (this.user) {
        this.profileImage = this.user.avatar_url || '';
        this.editedFirstName = this.user.first_name || '';
        this.editedLastName = this.user.last_name || '';
        this.newEmail = this.user.email || '';
        this.isEditing = false;
        this.isEditingEmail = false;
      }
    },
    applyChanges() {
      // Aquí deberías implementar la lógica para guardar los cambios en el backend
      alert('Cambios aplicados (simulado)');
      this.isEditing = false;
      this.isEditingEmail = false;
    }
  }
});
</script>

<style scoped>
.profile-container {
  max-width: 700px;
  margin: 2.5rem auto;
  padding: 2rem 1.5rem;
  background: #f6f6f8;
  border-radius: 24px;
  min-height: 400px;
  box-shadow: 0 2px 16px 0 rgba(0,0,0,0.04);
  border: 2px solid var(--primary);
}
.profile-header h1 {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 1.5rem 0;
  color: var(--primary);
}
.btn-primary {
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 0.5rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.btn-primary:hover {
  background: var(--primary-dark);
}
.btn-secondary {
  background: #fff;
  color: var(--primary);
  border: 2px solid var(--primary);
  border-radius: 20px;
  padding: 0.5rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}
.btn-secondary:hover {
  background: #f3eaff;
  color: var(--primary-dark);
}
input, textarea {
  border: 1px solid #d1d1d1;
  border-radius: 10px;
  padding: 0.7rem 1rem;
  font-size: 1rem;
  background: #fff;
  color: #222;
  outline: none;
  transition: border 0.2s;
}
input:focus, textarea:focus {
  border-color: var(--primary);
}
.profile-header {
  margin-bottom: 2rem;
}
.profile-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}
.profile-content {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}
.profile-section {
  display: flex;
  align-items: flex-start;
  gap: 2.5rem;
  margin-bottom: 0.5rem;
}
.section-title {
  min-width: 180px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #222;
  margin-top: 0.5rem;
}
.profile-photo-section {
  display: flex;
  align-items: center;
  gap: 2rem;
}
.profile-avatar-container {
  width: 120px;
  height: 120px;
  border-radius: 24px;
  overflow: hidden;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.profile-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 24px;
}
.profile-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: #bdbdbd;
}
.photo-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.btn-change-photo {
  background: #000;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 0.6rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 0.2rem;
  transition: background 0.2s;
}
.btn-change-photo:hover {
  background: #6c2eb7;
}
.photo-requirements {
  font-size: 0.85rem;
  color: #444;
  margin: 0;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.5rem;
}
.form-label {
  font-size: 1rem;
  font-weight: 500;
  color: #222;
}
.form-input {
  border: 1px solid #d1d1d1;
  border-radius: 10px;
  padding: 0.7rem 1rem;
  font-size: 1rem;
  background: #fff;
  color: #222;
  outline: none;
  transition: border 0.2s;
}
.form-input:focus {
  border-color: #a259e6;
}
.email-section {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 0.5rem;
}
.email-display {
  font-size: 1.05rem;
  color: #333;
  font-weight: 500;
}
.btn-modify-email {
  background: #000;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 0.6rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.btn-modify-email:hover {
  background: #6c2eb7;
}
.profile-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1.5rem;
  margin-top: 2.5rem;
}
.btn-discard {
  background: #fff;
  color: #000;
  border: 2px solid #a259e6;
  border-radius: 20px;
  padding: 0.6rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}
.btn-discard:hover {
  background: #f3eaff;
  color: #6c2eb7;
}
.btn-apply {
  background: #a259e6;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 0.6rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.btn-apply:hover {
  background: #6c2eb7;
}
.loading-state {
  text-align: center;
  color: #888;
  font-size: 1.1rem;
  margin-top: 3rem;
}
@media (max-width: 900px) {
  .profile-container {
    padding: 1.5rem 0.5rem;
  }
  .profile-section {
    flex-direction: column;
    gap: 1.2rem;
  }
  .profile-content {
    gap: 1.5rem;
  }
  .profile-header {
    margin-bottom: 1.2rem;
  }
}
</style>
